<template lang="pug">
#outing-list(v-cloak)  
  .box
    .box-header
      .d-flex
        .note.mr-2
          span 外出届けと外泊届けの受付ができます。各届け出の使用有無等は「基本設定」より設定してください。
          .register-row(v-show="errorMessage")
            .register-group
              .register-value
                .error-message {{ errorMessage }}
        .d-flex.flex-fill.justify-content-end.flex-wrap
          button.btn.setting-btn.mr-2(@click="goToSettings") 基本設定
          button.btn.register-btn(@click="goToRegister") 代理届け
      
    .box-body
      b-tabs
        b-tab.tab(title="届出一覧")
          OutingListTab(
            ref="outingListTab"
            @onDetail="onDetail"
            @onConsented="onConsented"
            @onRefuse="onRefuse"
            @onCancel="onCancel"
            @onMultipleConsented="onMultipleConsented"
          )
        b-tab.tab(title="食事申請状況")
          NeedsMealsTab(ref="needsMealsTab")
  //- 承諾モーダル
  OkCancelModal(v-model="showConsentedConfirmModal" :title="consentedConfirmModalMessage" @ok="consented")
  OkModal(v-model="showConsentedSuccessModal" title="承諾しました。")

  //- 辞退、キャンセル（メッセージ入力）モーダル（botから予約、代理で家族をひもづけて予約）
  MessageModal(v-model="showRefuseCancelModal"
    :modalTitle="refuseCancelModalTitle" :modalMessage="refuseCancelModalMessage"
    :title.sync="refuseCancelMessageTitle" :text.sync="refuseCancelMessageText"
    @onSend="confirmRefuseCancel")
  MessageConfirmModal(v-model="showRefuseCancelConfirmModal"
    :modalTitle="showRefuseCancelConfirmModalTitle"
    :title="refuseCancelMessageTitle" :text="refuseCancelMessageText"
    @onSend="refuseCancel")
  //- キャンセルモーダル（代理で家族をひもづけず予約）
  OkCancelModal(v-model="showRefuseCancelModalByNoMessage"
    :title="refuseCancelModalTitle" :message="refuseCancelModalMessage"
    @ok="refuseCancel")
  //- キャンセルモーダル（家族ユーザー削除済み）
  OkCancelModal(v-model="showRefuseCancelConfirmModalByDeletedFamily" title="家族ユーザーが削除されている面会予約ですが、キャンセルしますか？" @ok="refuseCancel")
  //- 辞退、キャンセル完了モーダル
  OkModal(v-model="showRefuseCancelSuccessModal" :title="getUpdateTypeName() + 'しました。'")

  //- 一括承諾モーダル
  MultipleConsentedModal(v-model="showMultipleConsentedModal" :items="updateTargetItems" @ok="multipleConsented")
  ProgressLoading(v-model="currentConsentedCount" :max="consentedCount" :isBusy="progressLoading" :message="progressMessage")
  OkModal(v-model="showMultipleConsentedSuccessModal" title="一括承諾が完了しました。")

  //- 画面全体ローディング
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Ref, Vue } from "vue-property-decorator";
import { Route } from "vue-router";
import { userStore } from "@/store/store";
import SystemError from "@/commons/models/SystemError";

import { PcUrl } from "@/commons/constants/PcUrl";

import OutingsService, { InputBy, OutingStatusPatchModel, UpdateOutingStatusType } from "@/commons/services/Outings";
import FamiliesService, { FamilyGetModel, SenderType } from "@/commons/services/Families";

import OutingListTab from "@/pc/containers/outings/OutingListTab.vue";
import { OutingTableItem } from "@/pc/containers/outings/OutingTable.vue";
import NeedsMealsTab from "@/pc/containers/outings/NeedsMealsTab.vue";

import PageLoading from "@/pc/basics/PageLoading.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import ProgressLoading from "@/pc/basics/ProgressLoading.vue";
import MessageModal from "@/pc/containers/outings/MessageModal.vue";
import MessageConfirmModal from "@/pc/containers/outings/MessageConfirmModal.vue";
import MultipleConsentedModal from "@/pc/containers/outings/MultipleConsentedModal.vue";
import { OutingType } from "@/commons/services/Outings";

/** ナビゲーションガードのフックを使えるようにする */
Component.registerHooks(["beforeRouteLeave"]);

@Component({
  components: {
    PageLoading,
    OkModal,
    OkCancelModal,
    ProgressLoading,
    MessageModal,
    MessageConfirmModal,
    MultipleConsentedModal,
    OutingListTab,
    NeedsMealsTab
  }
})
export default class OutingList extends Vue {
  /** 外出外泊サービス */
  @Inject("OutingsService") outingsService!: OutingsService;
  /** 家族ユーザーサービス */
  @Inject("FamiliesService") familiesService!: FamiliesService;

  @Ref() outingListTab!: OutingListTab;
  @Ref() needsMealsTab!: NeedsMealsTab;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** エラーメッセージ */
  errorMessage: string = "";

  /** 承諾確認モーダル表示可否 */
  showConsentedConfirmModal: boolean = false;
  /** 承諾確認モーダルメッセージ */
  consentedConfirmModalMessage: string = "";
  /** 承諾完了モーダル表示可否 */
  showConsentedSuccessModal: boolean = false;

  /** 辞退、キャンセルの種別 */
  updateOutingStatusType: UpdateOutingStatusType = UpdateOutingStatusType.UNKNOWN;
  /** 辞退、キャンセルモーダル表示可否 */
  showRefuseCancelModal: boolean = false;
  /** 辞退、キャンセルモーダルタイトル */
  refuseCancelModalTitle: string = "";
  /** 辞退、キャンセルモーダル本文 */
  refuseCancelModalMessage: string = "";
  /** 辞退、キャンセルモーダルの送信メッセージタイトル */
  refuseCancelMessageTitle: string = "";
  /** 辞退、キャンセルモーダルの送信メッセージ本文 */
  refuseCancelMessageText: string = "";
  /** 辞退、キャンセル確認モーダル表示可否 */
  showRefuseCancelConfirmModal: boolean = false;
  /** 辞退、キャンセル確認モーダルタイトル */
  showRefuseCancelConfirmModalTitle: string = "";
  /** 辞退、キャンセル（メッセージ無し）モーダル表示可否 */
  showRefuseCancelModalByNoMessage: boolean = false;
  /** 辞退、キャンセル（家族削除済み）モーダル表示可否 */
  showRefuseCancelConfirmModalByDeletedFamily: boolean = false;
  /** 辞退、キャンセル完了モーダル表示可否 */
  showRefuseCancelSuccessModal: boolean = false;

  /** 一括承諾モーダル表示可否 */
  showMultipleConsentedModal: boolean = false;
  /** プログレス付きローディング表示可否 */
  progressLoading: boolean = false;
  /** プログレス付きローディングメッセージ */
  progressMessage: string = "";
  /** 現在の一括承諾数 */
  currentConsentedCount: number = 0;
  /** 一括承諾数最大値 */
  consentedCount: number = 0;
  /** 一括承諾完了モーダル表示可否 */
  showMultipleConsentedSuccessModal: boolean = false;

  /** 更新対象の外出外泊届け */
  updateTargetItem: OutingTableItem | null = null;
  /** 一括承諾対象の外出外泊届け */
  updateTargetItems: Array<OutingTableItem> = [];

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    return;
  }

  /**
   * ### 詳細ボタン押下イベント
   */
  public onDetail(item: OutingTableItem): void {
    this.$router.push({ name: PcUrl.OutingDetail, params: { id: `${item.id}` } });
    return;
  }

  /**
   * ### 承諾ボタン押下イベント
   */
  public onConsented(item: OutingTableItem): void {
    const residentName: string = `${item.targetResidentInfo.name}（${item.targetResidentInfo.roomName}）`;
    const familyName: string = `${item.applicantFamilyInfo.name}（${item.applicantFamilyInfo.relation}）`;
    const typeName: string = item.type == OutingType.GOING_OUT ? "外出届け" : item.type == OutingType.STAYING_OUT ? "外泊届け" : "";
    const startDate: string = this.$dateUtil.convertNumberToString(item.startDatetime, "mm月dd日");
    const startTime: string = this.$dateUtil.convertNumberToString(item.startDatetime, "hh:MM");
    const endDate: string = this.$dateUtil.convertNumberToString(item.endDatetime, "mm月dd日");
    const endTime: string = this.$dateUtil.convertNumberToString(item.endDatetime, "hh:MM");
    const datetime: string =
      item.type == OutingType.GOING_OUT ? `${startDate} ${startTime}～${endTime}` : item.type == OutingType.STAYING_OUT ? `${startDate} ${startTime}～${endDate} ${endTime}` : "";
    const message: string = `${residentName}様の${familyName}の${datetime}の${typeName}を承諾しますか？`;
    this.consentedConfirmModalMessage = message;

    this.updateTargetItem = item;
    this.showConsentedConfirmModal = true;

    return;
  }

  /**
   * ### 承諾実施
   */
  public async consented(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "承諾中...";

    try {
      const item: OutingTableItem = this.updateTargetItem as OutingTableItem;
      const requestBody: OutingStatusPatchModel = {
        statusType: UpdateOutingStatusType.CONSENTED,
        text: "",
        title: "",
        updated: Number(item.updated)
      };
      await this.outingsService.patchStatus(userStore, item.id, requestBody);

      this.isBusy = false;
      this.showConsentedSuccessModal = true;

      // サイドメニューの未完了バッジを更新する
      this.$emit("refresh");

      // 画面情報再取得する
      await this.outingListTab.refresh();

      return;
    } catch (error) {
      const systemError: SystemError = error as SystemError;

      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode == 409) {
        await this.outingListTab.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw systemError;
      }
    } finally {
      this.isBusy = false;
    }
  }

  /**
   * ### 辞退ボタン押下イベント
   */
  public async onRefuse(item: OutingTableItem): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    this.updateTargetItem = item;
    this.updateOutingStatusType = UpdateOutingStatusType.REFUSE;

    await this.showRefuseCancelModals(item, UpdateOutingStatusType.REFUSE);

    this.isBusy = false;
    return;
  }

  /**
   * ### 辞退、キャンセルモーダルのいずれかを表示する
   */
  public async showRefuseCancelModals(item: OutingTableItem, statusType: UpdateOutingStatusType): Promise<void> {
    // モーダルタイトル等で使用する利用者、家族、種別を作成
    const residentName: string = `${item.targetResidentInfo.name}（${item.targetResidentInfo.roomName}）`;
    const familyName: string = `${item.applicantFamilyInfo.name}（${item.applicantFamilyInfo.relation}）`;
    const typeName: string = item.type == OutingType.GOING_OUT ? "外出届け" : item.type == OutingType.STAYING_OUT ? "外泊届け" : "";
    const operation: string = statusType == UpdateOutingStatusType.REFUSE ? "辞退" : statusType == UpdateOutingStatusType.CANCEL ? "キャンセル" : "";

    // 家族を指定していない代理予約の場合はメッセージモーダルではなく既存のモーダルを表示する
    const splitedResidentsSortKey: string[] = item.residentsSortKey.split("_");
    if (Number(splitedResidentsSortKey[1]) == 0 && item.inputBy == InputBy.FACILITY) {
      this.refuseCancelModalTitle = `${residentName}様の${familyName}の${typeName}を${operation}しますか？`;
      this.refuseCancelModalMessage = `登録されていないご家族からの${typeName}のため、自動通知がありません。お電話でのご連絡をお願いします。`;
      this.refuseCancelMessageTitle = "";
      this.refuseCancelMessageText = "";

      this.isBusy = false;
      this.showRefuseCancelModalByNoMessage = true;
    }
    // 代理予約以外の場合はメッセージモーダルを表示する
    else {
      // 家族が存在する場合、辞退、キャンセル（メッセージ入力）モーダルを表示する
      const family: FamilyGetModel | null = await this.getFamily(Number(splitedResidentsSortKey[0]), Number(splitedResidentsSortKey[1]));
      if (family) {
        const startDate: string = this.$dateUtil.convertNumberToString(item.startDatetime, "mm月dd日");
        const startTime: string = this.$dateUtil.convertNumberToString(item.startDatetime, "hh:MM");
        const endDate: string = this.$dateUtil.convertNumberToString(item.endDatetime, "mm月dd日");
        const endTime: string = this.$dateUtil.convertNumberToString(item.endDatetime, "hh:MM");
        const datetime: string =
          item.type == OutingType.GOING_OUT
            ? `${startDate} ${startTime}～${endTime}`
            : item.type == OutingType.STAYING_OUT
            ? `${startDate} ${startTime}～${endDate} ${endTime}`
            : "";

        // メッセージモーダルのタイトル、本文
        this.refuseCancelModalTitle = `${residentName}様の${familyName}の${typeName}を${operation}しますか？`;
        this.refuseCancelModalMessage = `ご家族へのメッセージ内容を編集して送信してください。\n送信すると${typeName}が取り消されます。`;
        // 送信メッセージのタイトル、本文の初期入力
        this.refuseCancelMessageTitle = `${typeName}の${operation}（${datetime}）`;
        const texts: string[] = [];
        if (family.senderType == SenderType.LINE) {
          // LINEメッセージ送信
          texts.push(`${userStore.facility.name}  ${family.name}様`);
          texts.push(`${datetime}の${typeName}が施設のご都合により${operation}されました。\n`);
          texts.push(`別の日程等を選択して再申請をお願いします。`);
        } else if (family.senderType == SenderType.MAIL) {
          // メール送信
          texts.push(`HitomeQ コネクト のご家族向けメール配信サービスです。\n`);
          texts.push(`${userStore.facility.name}のご都合により、${typeName}が${operation}されました。`);
          texts.push(`予約日時: ${datetime}\n`);
          texts.push(`別日程での再申請をご希望の場合は、${userStore.facility.name}へご連絡ください。\n\n`);
          texts.push(`このメールへの返信はできませんのでご了承ください。`);
          texts.push(`配信停止をご希望の場合は、ご利用の施設までご連絡ください。`);
        }
        this.refuseCancelMessageText = texts.join("\n");
        this.showRefuseCancelModal = true;
      }
      // 家族が削除されていた場合、辞退、キャンセル（家族ユーザー削除済み）モーダルを表示する
      else {
        this.isBusy = false;
        this.showRefuseCancelConfirmModalByDeletedFamily = true;
      }
    }
    return;
  }

  /**
   * ### 家族を取得する
   *
   * - 家族単一を取得するAPIはないので、利用者にひもづく家族を取得する
   * - 家族の中から家族IDが同一のものを修正対象の家族と判断する
   * - 見つからなかった場合は404画面に遷移させたいので例外を投げる
   */
  public async getFamily(residentId: number, familyId: number): Promise<FamilyGetModel | null> {
    const families: Array<FamilyGetModel> = await this.familiesService.getsInResident(userStore, residentId);
    for (const family of families) {
      if (family.familyId == familyId) {
        return family;
      }
    }
    return null;
  }

  /**
   * ### 辞退、キャンセルモーダル（メッセージ入力モーダル）の確認モーダル
   */
  public confirmRefuseCancel(): void {
    const item: OutingTableItem = this.updateTargetItem as OutingTableItem;
    const residentName: string = `${item.targetResidentInfo.name}（${item.targetResidentInfo.roomName}）`;
    const familyName: string = `${item.applicantFamilyInfo.name}（${item.applicantFamilyInfo.relation}）`;
    this.showRefuseCancelConfirmModalTitle = `${residentName}様の${familyName}に以下の内容でメッセージを送信します。よろしいですか？`;
    this.showRefuseCancelConfirmModal = true;
    return;
  }

  /**
   * ### 外出外泊届けの辞退、キャンセル
   */
  public async refuseCancel(): Promise<void> {
    const item: OutingTableItem = this.updateTargetItem as OutingTableItem;
    const typeName: string = item.type == OutingType.GOING_OUT ? "外出届け" : item.type == OutingType.STAYING_OUT ? "外泊届け" : "";
    const updateTypeName: string = this.getUpdateTypeName();

    this.isBusy = true;
    this.loadingMessage = `${typeName}の${updateTypeName}中...`;

    this.showRefuseCancelConfirmModal = false;
    this.showRefuseCancelModal = false;
    this.showRefuseCancelConfirmModal = false;
    this.showRefuseCancelConfirmModal = false;

    try {
      const requestBody: OutingStatusPatchModel = {
        statusType: this.updateOutingStatusType,
        text: this.refuseCancelMessageText,
        title: this.refuseCancelMessageTitle,
        updated: Number(item.updated)
      };
      await this.outingsService.patchStatus(userStore, item.id, requestBody);

      this.isBusy = false;
      this.showRefuseCancelSuccessModal = true;

      // サイドメニューの未完了バッジを更新する
      this.$emit("refresh");

      // 画面情報再取得する
      await this.outingListTab.refresh();

      return;
    } catch (error) {
      const systemError: SystemError = error as SystemError;

      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode == 409) {
        await this.outingListTab.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw systemError;
      }
    } finally {
      this.isBusy = false;
    }
  }

  private getUpdateTypeName(): string {
    switch (this.updateOutingStatusType) {
      case UpdateOutingStatusType.CONSENTED: {
        return "承諾";
      }
      case UpdateOutingStatusType.REFUSE: {
        return "辞退";
      }
      case UpdateOutingStatusType.CANCEL: {
        return "キャンセル";
      }
      default: {
        return "";
      }
    }
  }

  /**
   * ### キャンセルボタン押下イベント
   */
  public async onCancel(item: OutingTableItem): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    this.updateTargetItem = item;
    this.updateOutingStatusType = UpdateOutingStatusType.CANCEL;

    await this.showRefuseCancelModals(item, UpdateOutingStatusType.CANCEL);

    this.isBusy = false;
  }

  /**
   * ### 一括承諾ボタン押下イベント
   */
  public onMultipleConsented(items: Array<OutingTableItem>): void {
    this.updateTargetItems = items;
    this.showMultipleConsentedModal = true;
    return;
  }

  /**
   * ### 一括承諾する
   */
  public async multipleConsented(): Promise<void> {
    try {
      // プログレス付きモーダルを表示する
      // 進捗はファイルのアプロード状況を反映させるようにする
      this.currentConsentedCount = 0;
      this.consentedCount = this.updateTargetItems.length;
      this.progressMessage = "一括承諾中。\n完了するまでそのままお待ちください。\n承諾中に別画面に遷移しますと処理がキャンセルされます。";
      this.progressLoading = true;

      // 承諾を実施する
      for (let i = 0; i < this.updateTargetItems.length; i++) {
        this.currentConsentedCount = i;
        const requestBody: OutingStatusPatchModel = {
          statusType: UpdateOutingStatusType.CONSENTED,
          text: "",
          title: "",
          updated: Number(this.updateTargetItems[i].updated)
        };
        await this.outingsService.patchStatus(userStore, this.updateTargetItems[i].id, requestBody);

        // 一覧の表示切替のためにitemを更新する
        this.updateTargetItems[i].consented = true;
        this.updateTargetItems[i].consentedUserId = userStore.loginUser.userId;
        this.updateTargetItems[i].consentedDatetime = this.$dateUtil.convertDateToNumber(new Date(), "yyyymmddhhMMss000");
      }

      this.progressLoading = false;
      this.showMultipleConsentedSuccessModal = true;

      // サイドメニューの未完了バッジを更新する
      this.$emit("refresh");

      // 画面情報再取得する
      await this.outingListTab.refresh();

      return;
    } catch (error) {
      const systemError: SystemError = error as SystemError;

      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode == 409) {
        await this.outingListTab.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw systemError;
      }
    } finally {
      this.progressLoading = false;
    }
  }

  /**
   * ### 設定画面へ遷移する
   */
  public goToSettings(): void {
    this.$router.push(PcUrl.OutingSetting);
    return;
  }

  /**
   * ### 登録画面へ遷移する
   */
  public goToRegister(): void {
    this.$router.push(PcUrl.OutingRegister);
    return;
  }

  beforeRouteLeave(to: Route, from: Route, next: Function) {
    // 画面を離れる前に現在のフィルタリング条件をVuexに保存
    if (this.$options.name) {
      this.outingListTab.setFilterSetting();
      this.needsMealsTab.setFilterSetting();
    }
    next();
  }
}
</script>

<style lang="scss" scoped>
#outing-list {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;

  .setting-btn,
  .register-btn {
    min-width: 120px;
  }
}
</style>
